import React, { useEffect, useRef, useState } from "react";
import dayjs, { Dayjs } from "dayjs";
import { motion } from "framer-motion";
import { toast } from "react-toastify";
import { ColDef } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { CircularProgress, IconButton, Menu, MenuItem } from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import Calendar from "../../../components/calendar/Calender";
import axiosInterceptorInstance from "../../../utils/axios.interceptor";

const WelcomeOfferTopUp = () => {
  const [rowData, setRowData] = useState<any[]>([]);
  const agGridRef = useRef<AgGridReact | null>(null);
  const [selectedStartDate, setSelectedStartDate] = useState<Dayjs | null>(
    dayjs().add(-30, "days")
  );
  const [selectedEndDate, setSelectedEndDate] = useState<Dayjs | null>(dayjs());
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [campaignDataLoading, setCampaignDataLoading] =
    useState<boolean>(false);

  const controller = new AbortController();

  useEffect(() => {
    return () => {
      controller.abort();
    };
  }, []);

  useEffect(() => {
    if (selectedStartDate && selectedEndDate) {
      const startDateParam = selectedStartDate.format("YYYY-MM-DD");
      const endDateParam = selectedEndDate.add(1, "days").format("YYYY-MM-DD");
      setRowData([]);
      fetchDataFromAPI(startDateParam || "", endDateParam || "");
    }
  }, [selectedStartDate, selectedEndDate]);

  const fetchDataFromAPI = async (startDate: string, endDate: string) => {
    setCampaignDataLoading(true);

    try {
      const response = await axiosInterceptorInstance.get(
        `/ThaCrm/welcomeOfferInternationalTopUp?StartDate=${startDate}&EndDate=${endDate}`
      );

      const responseData = JSON.parse(response?.data.body);
      if (!responseData?.Data || responseData.Data.length === 0) {
        toast.info("No  Data found for the selected Date Range");
        setRowData([]);
        return;
      }
      setRowData(responseData.Data);
    } catch (error) {
      console.error(error);
      toast.error("Failed to fetch data");
    } finally {
      setCampaignDataLoading(false);
    }
  };

  const exportGridDataAsCSV = () => {
    if (agGridRef.current) {
      agGridRef.current.api.exportDataAsCsv();
    }
  };

  const columnDefs: ColDef[] = [
    {
      field: "Name",
      headerName: "Name",
      filter: true,
      sortable: true,
      resizable: true,
    },
    {
      field: "Country",
      headerName: "Country",
      filter: true,
      sortable: true,
      resizable: true,
    },
    {
      field: "Currency",
      headerName: "Currency",
      filter: true,
      sortable: true,
      resizable: true,
    },
    {
      field: "OperatorShortCode",
      headerName: "OperatorShortCode",
      filter: true,
      sortable: true,
      resizable: true,
    },
    {
      field: "ProductId",
      headerName: "ProductId",
      filter: true,
      sortable: true,
      resizable: true,
    },
    {
      field: "DiscountedPrice",
      headerName: "DiscountedPrice",
      filter: true,
      sortable: true,
      resizable: true,
    },
    {
      field: "DestinationPrice",
      headerName: "DestinationPrice",
      filter: true,
      sortable: true,
      resizable: true,
    },
    {
      field: "DestinationCurrency",
      headerName: "DestinationCurrency",
      filter: true,
      sortable: true,
      resizable: true,
    },
    {
      field: "ActivationType",
      headerName: "ActivationType",
      filter: true,
      sortable: true,
      resizable: true,
    },
    {
      field: "OriginationCountry",
      headerName: "OriginationCountry",
      filter: true,
      sortable: true,
      resizable: true,
    },
    {
      field: "CustomerType",
      headerName: "CustomerType",
      filter: true,
      sortable: true,
      resizable: true,
    },
  ];

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const toggleCalendar = () => {
    setCalendarOpen(!calendarOpen);
    handleMenuClose();
  };

  const handleStartDateChange = (date: Dayjs | null) => {
    if (date !== null && date.isValid()) {
      setSelectedStartDate(date);
    }
  };

  const handleEndDateChange = (date: Dayjs | null) => {
    if (date !== null && date.isValid()) {
      setSelectedEndDate(date);
    }
  };
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ ease: "linear", duration: 0.5 }}
    >
      <div>
        <h2 className="heading">WelcomeOffer International Topup</h2>
        {selectedEndDate &&
          selectedStartDate &&
          Math.floor(selectedEndDate.diff(selectedStartDate, "day", true)) ===
            30 && <span className="small-text">Last 30 Days</span>}
      </div>

      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          margin: "16px 0 5px 0",
        }}
      >
        <button
          className="small-btn"
          style={{ visibility: rowData?.length > 0 ? "visible" : "hidden" }}
          onClick={() => {
            if (rowData) {
              exportGridDataAsCSV();
            }
          }}
        >
          Export as CSV
        </button>

        <IconButton
          onClick={(e: any) => {
            handleMenuClick(e);
          }}
        >
          <MoreVertIcon />
        </IconButton>
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
        >
          <MenuItem onClick={toggleCalendar}></MenuItem>
          {selectedStartDate && selectedEndDate && (
            <Calendar
              selectedStartDate={selectedStartDate}
              selectedEndDate={selectedEndDate}
              onStartDateChange={handleStartDateChange}
              onEndDateChange={handleEndDateChange}
            />
          )}
        </Menu>
      </div>

      {campaignDataLoading && (
        <div className="dummy-conatiner">
          <CircularProgress size={40} />
        </div>
      )}

      {rowData?.length > 0 && !campaignDataLoading && (
        <div className="ag-theme-alpine">
          <AgGridReact
            ref={agGridRef}
            paginationPageSize={12}
            pagination={true}
            domLayout="autoHeight"
            rowData={rowData}
            columnDefs={columnDefs}
            enableCellTextSelection={true}
          ></AgGridReact>
        </div>
      )}
    </motion.div>
  );
};

export default WelcomeOfferTopUp;
